import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getEmployees } from '../services/employeeService';
import { taskService } from '../services/taskService';
import { projectService } from '../services/projectService';
import AssignmentForm from '../components/AssignmentForm';
import TasksDayView, { DayAssignment } from '../components/tasks/TasksDayView';
import ProjectAlerts from '../components/projects/ProjectAlerts';
import { getSchedule } from '../components/projects/projectSchedule';
import { Button, Select, Modal, Form, Popconfirm, Tooltip, Skeleton, Row, Col } from 'antd';
import {
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  ScheduleOutlined,
  TeamOutlined,
  ProjectOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import MainLayout from '../components/MainLayout';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PageHeader, SectionCard, EmptyState, FilterChips, PersonAvatar, Pill } from '../components/ui';
import './TasksPage.css';

// 'day' is the default: everyone's assignments on one day; the other two are month calendars
type ViewMode = 'day' | 'employee' | 'project';

interface Assignment {
  id: number;
  date: string;
  project_id: number;
  project?: { id: number; name: string; status?: string };
  employee?: { id: number; name: string; surname?: string; imageUrl?: string | null };
}

/** A colour per project, so the same project looks the same on every day of the month. */
const PROJECT_COLORS = ['#0A7EA4', '#7C3AED', '#DB2777', '#EA580C', '#16A34A', '#2563EB', '#B45309', '#0F766E'];
const colorFor = (projectId: number) => PROJECT_COLORS[projectId % PROJECT_COLORS.length];

const TasksPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [employees, setEmployees] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string | number>('');
  const [selectedProject, setSelectedProject] = useState<string | number>('');
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [dayDate, setDayDate] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [dayViewAssignments, setDayViewAssignments] = useState<DayAssignment[]>([]);
  const [dayLoading, setDayLoading] = useState(true);
  const [dayAssignRequest, setDayAssignRequest] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();

  useEffect(() => {
    getEmployees()
      .then(setEmployees)
      .catch(() => toast.error(t('employees.fetchError')));
    projectService
      .getProjects()
      .then(setProjects)
      .catch(() => toast.error(t('projects.fetchError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssignments = useCallback(() => {
    const month = selectedMonth.month() + 1;
    const year = selectedMonth.year();

    if (viewMode === 'employee' && selectedEmployee) {
      setLoading(true);
      taskService
        .getMonthlyAssignments(String(selectedEmployee), month, year)
        .then(setAssignments)
        .catch(() => setAssignments([]))
        .finally(() => setLoading(false));
    } else if (viewMode === 'project' && selectedProject) {
      setLoading(true);
      projectService
        .getMonthlyAssignments(selectedProject, month, year)
        .then(setAssignments)
        .catch(() => setAssignments([]))
        .finally(() => setLoading(false));
    } else {
      setAssignments([]);
      setLoading(false);
    }
  }, [viewMode, selectedEmployee, selectedProject, selectedMonth]);

  useEffect(() => {
    if (viewMode !== 'day') fetchAssignments();
  }, [viewMode, fetchAssignments]);

  const fetchDay = useCallback(() => {
    setDayLoading(true);
    taskService
      .getDailyAssignments(dayDate)
      .then(setDayViewAssignments)
      .catch(() => {
        setDayViewAssignments([]);
        toast.error(t('tasks.fetchDayFailed'));
      })
      .finally(() => setDayLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayDate]);

  useEffect(() => {
    if (viewMode === 'day') fetchDay();
  }, [viewMode, fetchDay]);

  const refreshCurrent = () => (viewMode === 'day' ? fetchDay() : fetchAssignments());

  // From the day view into a month calendar, on the month of the day being looked at
  const openMonthFromDay = (mode: 'employee' | 'project', id: number) => {
    if (mode === 'employee') {
      setSelectedEmployee(id);
      setSelectedProject('');
    } else {
      setSelectedProject(id);
      setSelectedEmployee('');
    }
    setAssignments([]);
    setSelectedMonth(dayjs(dayDate).startOf('month'));
    setSelectedDay(dayDate);
    setViewMode(mode);
  };

  // From a start / end alert into that project's month calendar, on the day the alert is about
  const openProjectFromAlert = (projectId: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const schedule = getSchedule(project);
    const day = (schedule.state === 'startsSoon' ? schedule.start : schedule.end) ?? dayjs();
    setSelectedProject(projectId);
    setSelectedEmployee('');
    setAssignments([]);
    setSelectedMonth(day.startOf('month'));
    setSelectedDay(day.format('YYYY-MM-DD'));
    setViewMode('project');
  };

  const handleDeleteAssignment = async () => {
    if (!selectedAssignment) return;
    try {
      setSaving(true);
      await taskService.deleteAssignment(selectedAssignment.id);
      toast.success(t('tasks.assignmentDeleted'));
      setEditModalOpen(false);
      refreshCurrent();
    } catch (e) {
      toast.error(t('tasks.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAssignment = async (values: any) => {
    if (!selectedAssignment) return;
    try {
      setSaving(true);
      await taskService.updateAssignmentProject(selectedAssignment.id, values.project_id);
      toast.success(t('tasks.assignmentUpdated'));
      setEditModalOpen(false);
      refreshCurrent();
    } catch (e) {
      toast.error(t('tasks.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const openAssignment = (assignment: Assignment) => {
    // The day and employee views can move an assignment to another project (same as the mobile app)
    if (viewMode === 'project') return;
    setSelectedAssignment(assignment);
    editForm.setFieldsValue({ project_id: assignment.project_id });
    setEditModalOpen(true);
  };

  // ---------- Calendar ----------
  const byDate = useMemo(() => {
    const map = new Map<string, Assignment[]>();
    assignments.forEach((assignment) => {
      const key = dayjs(assignment.date).format('YYYY-MM-DD');
      map.set(key, [...(map.get(key) ?? []), assignment]);
    });
    return map;
  }, [assignments]);

  /** Monday-first grid covering the whole month, padded with the neighbouring days. */
  const weeks = useMemo(() => {
    const first = selectedMonth.startOf('month');
    const start = first.subtract((first.day() + 6) % 7, 'day');
    const result: dayjs.Dayjs[][] = [];
    let cursor = start;
    for (let week = 0; week < 6; week++) {
      const days: dayjs.Dayjs[] = [];
      for (let day = 0; day < 7; day++) {
        days.push(cursor);
        cursor = cursor.add(1, 'day');
      }
      result.push(days);
      if (cursor.isAfter(selectedMonth.endOf('month'))) break;
    }
    return result;
  }, [selectedMonth]);

  const weekdayNames = useMemo(() => {
    const monday = dayjs().startOf('week').add(dayjs().startOf('week').day() === 0 ? 1 : 0, 'day');
    return Array.from({ length: 7 }, (_, index) =>
      monday.add(index, 'day').toDate().toLocaleDateString(i18n.language, { weekday: 'short' }),
    );
  }, [i18n.language]);

  const monthLabel = selectedMonth.toDate().toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const hasSubject = viewMode === 'employee' ? !!selectedEmployee : !!selectedProject;
  const dayAssignments = selectedDay ? byDate.get(selectedDay) ?? [] : [];
  const assignedDays = byDate.size;

  const currentEmployee = employees.find((item) => String(item.id) === String(selectedEmployee));

  const subjectLabel = () => {
    if (viewMode === 'employee') {
      return currentEmployee ? `${currentEmployee.name} ${currentEmployee.surname}` : '';
    }
    const project = projects.find((item) => String(item.id) === String(selectedProject));
    return project?.name ?? '';
  };

  return (
    <MainLayout bare>
      <PageHeader
        title={t('tasks.title')}
        subtitle={
          viewMode === 'day'
            ? t('tasks.daySubtitle')
            : hasSubject && !loading
              ? `${subjectLabel()} · ${t('tasks.assignedDays', { count: assignedDays })}`
              : t('tasks.pickSubject')
        }
        actions={
          <>
            <Button
              icon={<ReloadOutlined spin={viewMode === 'day' ? dayLoading : loading} />}
              onClick={refreshCurrent}
              disabled={viewMode === 'day' ? dayLoading : !hasSubject || loading}
            >
              {t('dashboard.refresh')}
            </Button>
            {viewMode === 'day' ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setDayAssignRequest((n) => n + 1)}>
                {t('tasks.assignTask')}
              </Button>
            ) : (
              <Tooltip title={selectedEmployee ? undefined : t('tasks.selectEmployeeFirst')}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAssignModalOpen(true)}
                  disabled={!selectedEmployee}
                >
                  {t('tasks.assignTask')}
                </Button>
              </Tooltip>
            )}
          </>
        }
      />

      <ProjectAlerts projects={projects} onSelect={openProjectFromAlert} />

      <div className="tsk-controls e22-card">
        <FilterChips<ViewMode>
          ariaLabel={t('tasks.title')}
          value={viewMode}
          onChange={(mode) => {
            setViewMode(mode);
            setAssignments([]);
            setSelectedDay(null);
            if (mode === 'employee') setSelectedProject('');
            else if (mode === 'project') setSelectedEmployee('');
          }}
          options={[
            { key: 'day', label: t('tasks.byDay') },
            { key: 'employee', label: t('tasks.byEmployee') },
            { key: 'project', label: t('tasks.byProject') },
          ]}
        />

        {viewMode === 'day' ? null : viewMode === 'employee' ? (
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('tasks.selectEmployee')}
            value={selectedEmployee || undefined}
            onChange={(value) => {
              setSelectedEmployee(value);
              setSelectedDay(null);
            }}
            style={{ width: 260 }}
            options={employees.map((employee) => ({
              value: employee.id,
              label: `${employee.name} ${employee.surname}`,
            }))}
          />
        ) : (
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('tasks.selectProject')}
            value={selectedProject || undefined}
            onChange={(value) => {
              setSelectedProject(value);
              setSelectedDay(null);
            }}
            style={{ width: 260 }}
            options={projects.map((project) => ({
              value: project.id,
              label:
                project.status === 'active'
                  ? project.name
                  : `${project.name} (${t(`projects.statuses.${project.status}`)})`,
            }))}
          />
        )}

        {viewMode !== 'day' && (
        <div className="tsk-month">
          <Tooltip title={t('attendance.previousMonth')}>
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => {
                setSelectedMonth(selectedMonth.subtract(1, 'month'));
                setSelectedDay(null);
              }}
              aria-label={t('attendance.previousMonth')}
            />
          </Tooltip>
          <span className="tsk-month-label">{monthLabel}</span>
          <Tooltip title={t('attendance.nextMonth')}>
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={() => {
                setSelectedMonth(selectedMonth.add(1, 'month'));
                setSelectedDay(null);
              }}
              aria-label={t('attendance.nextMonth')}
            />
          </Tooltip>
        </div>
        )}
      </div>

      {viewMode === 'day' ? (
        <TasksDayView
          date={dayDate}
          assignments={dayViewAssignments}
          employees={employees}
          projects={projects}
          loading={dayLoading}
          colorFor={colorFor}
          onDateChange={setDayDate}
          onEdit={(assignment) => openAssignment(assignment)}
          onOpenMonth={openMonthFromDay}
          onAssigned={fetchDay}
          assignRequest={dayAssignRequest}
        />
      ) : (
      <Row gutter={[20, 20]}>
        <Col xs={24} xl={16}>
          <SectionCard title={monthLabel} icon={<ScheduleOutlined />}>
            {!hasSubject ? (
              <EmptyState
                icon={viewMode === 'employee' ? <TeamOutlined /> : <ProjectOutlined />}
                text={viewMode === 'employee' ? t('tasks.selectEmployee') : t('tasks.selectProject')}
              />
            ) : loading ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <div className="tsk-calendar">
                <div className="tsk-weekdays">
                  {weekdayNames.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </div>
                {weeks.map((week, weekIndex) => (
                  <div className="tsk-week" key={weekIndex}>
                    {week.map((day) => {
                      const key = day.format('YYYY-MM-DD');
                      const items = byDate.get(key) ?? [];
                      const outside = day.month() !== selectedMonth.month();
                      return (
                        <button
                          type="button"
                          key={key}
                          className={[
                            'tsk-day',
                            outside ? 'tsk-day--outside' : '',
                            day.isSame(dayjs(), 'day') ? 'tsk-day--today' : '',
                            selectedDay === key ? 'tsk-day--selected' : '',
                          ].join(' ')}
                          onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                        >
                          <span className="tsk-day-number">{day.format('D')}</span>
                          <span className="tsk-day-items">
                            {items.slice(0, 3).map((item) => (
                              <span
                                key={item.id}
                                className="tsk-chip"
                                style={{
                                  background: `${colorFor(item.project_id)}1a`,
                                  color: colorFor(item.project_id),
                                }}
                                title={
                                  viewMode === 'employee'
                                    ? item.project?.name
                                    : `${item.employee?.name} ${item.employee?.surname}`
                                }
                              >
                                {viewMode === 'employee'
                                  ? item.project?.name
                                  : `${item.employee?.name} ${item.employee?.surname}`}
                              </span>
                            ))}
                            {items.length > 3 ? <span className="tsk-more">+{items.length - 3}</span> : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </Col>

        <Col xs={24} xl={8}>
          <SectionCard
            title={
              selectedDay
                ? dayjs(selectedDay).toDate().toLocaleDateString(i18n.language, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })
                : t('tasks.wholeMonth')
            }
            icon={<ScheduleOutlined />}
            subtitle={
              selectedDay
                ? t('tasks.assignmentCount', { count: dayAssignments.length })
                : t('tasks.assignmentCount', { count: assignments.length })
            }
            action={
              selectedDay ? (
                <button type="button" className="e22-link-button" onClick={() => setSelectedDay(null)}>
                  {t('tasks.wholeMonth')}
                </button>
              ) : undefined
            }
          >
            {!hasSubject ? (
              <EmptyState icon={<ScheduleOutlined />} text={t('tasks.pickSubject')} />
            ) : (selectedDay ? dayAssignments : assignments).length === 0 ? (
              <EmptyState icon={<ScheduleOutlined />} text={t('tasks.noAssignments')} />
            ) : (
              <div className="e22-scroll">
                {(selectedDay ? dayAssignments : [...assignments].sort((a, b) => a.date.localeCompare(b.date))).map(
                  (assignment) => (
                    <div
                      key={assignment.id}
                      className={`e22-row ${viewMode === 'employee' ? 'tsk-row--clickable' : ''}`}
                      onClick={() => openAssignment(assignment)}
                      role={viewMode === 'employee' ? 'button' : undefined}
                    >
                      {viewMode === 'employee' ? (
                        <span
                          className="tsk-dot"
                          style={{ background: colorFor(assignment.project_id) }}
                          aria-hidden
                        />
                      ) : (
                        <PersonAvatar
                          name={assignment.employee?.name}
                          surname={assignment.employee?.surname}
                          imageUrl={assignment.employee?.imageUrl}
                          size={34}
                        />
                      )}
                      <div className="e22-row-body">
                        <div className="e22-row-title">
                          {viewMode === 'employee'
                            ? assignment.project?.name
                            : `${assignment.employee?.name} ${assignment.employee?.surname}`}
                        </div>
                        <div className="e22-row-subtitle">
                          {dayjs(assignment.date).toDate().toLocaleDateString(i18n.language, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}
                        </div>
                      </div>
                      {assignment.project?.status && assignment.project.status !== 'active' ? (
                        <Pill>{t(`projects.statuses.${assignment.project.status}`)}</Pill>
                      ) : null}
                    </div>
                  ),
                )}
              </div>
            )}
          </SectionCard>
        </Col>
      </Row>
      )}

      <AssignmentForm
        visible={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        employeeId={selectedEmployee}
        employeeName={
          currentEmployee
            ? { name: currentEmployee.name, surname: currentEmployee.surname, imageUrl: currentEmployee.thumbnailUrl }
            : undefined
        }
        defaultDate={selectedDay}
        month={selectedMonth}
        onAssigned={(firstDate) => {
          // Show the month the new days are in; changing the month refetches on its own
          if (firstDate && !dayjs(firstDate).isSame(selectedMonth, 'month')) {
            setSelectedMonth(dayjs(firstDate).startOf('month'));
            setSelectedDay(null);
          } else {
            fetchAssignments();
          }
        }}
      />

      <Modal
        title={t('tasks.editAssignment')}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        footer={null}
      >
        {selectedAssignment && (
          <>
            <div className="tsk-edit-subject">
              <span className="tsk-dot" style={{ background: colorFor(selectedAssignment.project_id) }} aria-hidden />
              <div className="e22-row-body">
                <div className="e22-row-title">{selectedAssignment.project?.name}</div>
                <div className="e22-row-subtitle">
                  {/* The day view mixes people, so say whose assignment it is */}
                  {viewMode === 'day' && selectedAssignment.employee
                    ? `${selectedAssignment.employee.name} ${selectedAssignment.employee.surname ?? ''}`.trim() + ' · '
                    : ''}
                  {dayjs(selectedAssignment.date).toDate().toLocaleDateString(i18n.language, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              </div>
            </div>
            <Form form={editForm} layout="vertical" onFinish={handleUpdateAssignment}>
              <Form.Item
                name="project_id"
                label={t('tasks.selectNewProject')}
                rules={[{ required: true, message: t('assignment.validation.project') }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('tasks.selectProject')}
                  options={projects
                    .filter((project) => project.status === 'active')
                    .map((project) => ({ value: project.id, label: project.name }))}
                />
              </Form.Item>
              <div className="tsk-edit-actions">
                <Popconfirm
                  title={t('tasks.deleteConfirm')}
                  onConfirm={handleDeleteAssignment}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  okButtonProps={{ danger: true }}
                >
                  <Button danger loading={saving}>
                    {t('common.delete')}
                  </Button>
                </Popconfirm>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t('tasks.updateProject')}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal>
    </MainLayout>
  );
};

export default TasksPage;
